import { useEffect, useRef, useState } from 'react';
import { getFundCompanyName } from '../../api/fundScreener.api';
import { FundCompanySearchItem } from '../../types/fundScreener.type';

// Empty keyword returns nothing from the BE, so seed with "a" for a real
// starting list instead of a blank dropdown.
const OPEN_SEED_KEYWORD = 'a';

// Debounced wrapper around the management company search endpoint.
export const useFundCompanySearch = (token: string, market: string) => {
  const [options, setOptions] = useState<FundCompanySearchItem[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const fetchOptions = (keyword: string) => {
    getFundCompanyName(keyword, market, token)
      .then(setOptions)
      .catch(() => setOptions([]));
  };

  const search = (keyword: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Falls back to the default list instead of clearing — covers both
    // "never typed anything" and "deleted everything back to empty".
    if (keyword.trim().length === 0) {
      fetchOptions(OPEN_SEED_KEYWORD);
      return;
    }

    debounceRef.current = setTimeout(() => fetchOptions(keyword), 300);
  };

  // Clicking into an already-empty input should show the default list
  // immediately, no debounce.
  const openWithCurrentKeyword = (keyword: string) => {
    if (keyword.trim().length > 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchOptions(OPEN_SEED_KEYWORD);
  };

  const clear = () => setOptions([]);

  return { options, search, openWithCurrentKeyword, clear };
};
